/**
 * VprRoomEditor Component
 *
 * Modal form for adding rooms with drag-drop image upload.
 * Same pattern as RoomRecognition/RoomEditor.jsx but create-only
 * (VPR doesn't support incremental image add).
 */

import React, { useState, useCallback, useRef } from 'react';
import {
  X,
  Upload,
  Image as ImageIcon,
  AlertCircle,
  Cpu,
  CheckCircle
} from 'lucide-react';

const VprRoomEditor = ({
  stops = [],
  guideId,
  guideName,
  onSave,
  onClose,
  isSaving
}) => {
  const fileInputRef = useRef(null);

  // Form state
  const [formData, setFormData] = useState({
    roomName: '',
    stopId: '',
    floor: '',
    description: ''
  });

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [errors, setErrors] = useState({});
  const [dragActive, setDragActive] = useState(false);

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  }, [errors]);

  const handleFileSelect = useCallback((files) => {
    // Keep every selected file visible. Images are decoded and resized when
    // saving, so filename, MIME metadata, and original file size should not
    // silently remove a reference image from the user's selection.
    const filesToAdd = Array.from(files);

    if (filesToAdd.length === 0) {
      setErrors(prev => ({ ...prev, images: 'Please select at least one image file' }));
      return;
    }

    const totalCount = selectedFiles.length + filesToAdd.length;
    if (totalCount > 20) {
      setErrors(prev => ({ ...prev, images: 'Maximum 20 images allowed' }));
      return;
    }

    const newPreviews = filesToAdd.map(file => ({
      file,
      url: URL.createObjectURL(file),
      name: file.name
    }));

    setSelectedFiles(prev => [...prev, ...filesToAdd]);
    setPreviews(prev => [...prev, ...newPreviews]);

    if (errors.images) {
      setErrors(prev => {
        const next = { ...prev };
        delete next.images;
        return next;
      });
    }
  }, [selectedFiles, errors]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragActive(false);
    handleFileSelect(e.dataTransfer.files);
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setDragActive(false);
  }, []);

  const handleRemoveImage = useCallback((index) => {
    URL.revokeObjectURL(previews[index].url);
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setPreviews(prev => prev.filter((_, i) => i !== index));
  }, [previews]);

  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.roomName.trim()) {
      newErrors.roomName = 'Room name is required';
    }

    if (!formData.stopId) {
      newErrors.stopId = 'Link this room to a stop in the current language';
    }

    if (selectedFiles.length === 0) {
      newErrors.images = 'At least one reference image is required';
    }

    if (selectedFiles.length > 0 && selectedFiles.length < 3) {
      newErrors.images = 'At least 3 reference images are required for V2';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, selectedFiles]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const roomData = {
      roomName: formData.roomName.trim(),
      stopId: formData.stopId || null,
      audioGuideId: guideId
    };

    const metadata = {};
    if (formData.floor) metadata.floor = formData.floor;
    if (formData.description) metadata.description = formData.description;

    await onSave(roomData, selectedFiles, metadata);
  }, [formData, selectedFiles, guideId, validateForm, onSave]);

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-auto">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full flex flex-col my-auto" style={{ maxHeight: 'calc(100vh - 2rem)' }}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
              <Cpu className="w-5 h-5 text-cyan-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Add VPR Room</h2>
              <p className="text-sm text-gray-500">{guideName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto flex-1">
          <div className="space-y-6">
            {/* Room Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Room Name *
              </label>
              <input
                type="text"
                value={formData.roomName}
                onChange={(e) => handleChange('roomName', e.target.value)}
                placeholder="e.g., Main Hall, Egyptian Gallery, Room 3"
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent ${
                  errors.roomName ? 'border-red-300' : 'border-gray-200'
                }`}
              />
              {errors.roomName && (
                <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.roomName}
                </p>
              )}
            </div>

            {/* Associate with Stop */}
            {stops.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Link to Stop (Optional)
                </label>
                <select
                  value={formData.stopId}
                  onChange={(e) => handleChange('stopId', e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent ${
                    errors.stopId ? 'border-red-300' : 'border-gray-200'
                  }`}
                >
                  <option value="">-- No linked stop --</option>
                  {stops.map((stop) => (
                    <option key={stop.id || stop.stopId} value={stop.id || stop.stopId}>
                      {stop.name || stop.title || `Stop ${stop.order || stop.id}`}
                    </option>
                  ))}
                </select>
                {errors.stopId && (
                  <p className="mt-1 text-sm text-red-600">{errors.stopId}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  When this room is recognized via VPR, the linked stop's audio will play
                </p>
              </div>
            )}

            {/* Floor */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Floor (Optional)
              </label>
              <input
                type="text"
                value={formData.floor}
                onChange={(e) => handleChange('floor', e.target.value)}
                placeholder="e.g., Ground Floor, Level 2"
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
              />
            </div>

            {/* Reference Images */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reference Images *
              </label>
              <p className="text-sm text-gray-500 mb-3">
                Upload 3-20 photos of the room from different angles. More variety = better CLIP matching.
              </p>
              <div className="text-xs text-gray-500 mb-3 bg-gray-50 rounded-lg p-3">
                <p className="font-medium text-gray-700 mb-1">Recommended set (6-10 photos):</p>
                <ul className="list-disc pl-4 space-y-0.5">
                  <li>Four wide views from different corners</li>
                  <li>Two views from likely visitor entrances</li>
                  <li>Two views emphasizing fixed, distinctive features</li>
                  <li>Add lighting variants if the museum regularly changes lighting</li>
                  <li>Keep most views clear; add one or two normal crowd views if the room is usually busy</li>
                </ul>
              </div>

              {/* Drop Zone */}
              <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  dragActive
                    ? 'border-cyan-500 bg-cyan-50'
                    : errors.images
                    ? 'border-red-300 bg-red-50'
                    : 'border-gray-200 hover:border-cyan-300 hover:bg-cyan-50'
                }`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => handleFileSelect(e.target.files)}
                  className="hidden"
                />

                <Upload className={`w-10 h-10 mx-auto mb-3 ${
                  dragActive ? 'text-cyan-500' : 'text-gray-400'
                }`} />

                <p className="text-sm text-gray-600 mb-2">
                  Drag & drop images here, or{' '}
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="text-cyan-600 font-medium hover:underline"
                  >
                    browse files
                  </button>
                </p>
                <p className="text-xs text-gray-400">
                  Images are resized automatically · Max 20 images · 3+ recommended
                </p>
              </div>

              {errors.images && (
                <p className={`mt-2 text-sm flex items-center gap-1 ${
                  errors.images.includes('recommended') ? 'text-yellow-600' : 'text-red-500'
                }`}>
                  <AlertCircle className="w-4 h-4" />
                  {errors.images}
                </p>
              )}

              {/* Image Previews */}
              {previews.length > 0 && (
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-gray-700">
                      Selected Images ({previews.length})
                    </p>
                    {previews.length >= 3 && (
                      <span className="text-xs text-green-600 flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" />
                        Good coverage
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-4 gap-3">
                    {previews.map((preview, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={preview.url}
                          alt={preview.name}
                          className="w-full h-24 object-cover rounded-lg border border-gray-200"
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveImage(index)}
                          className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Tips */}
              <div className="mt-4 bg-cyan-50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-cyan-800 mb-2">Tips for best CLIP matching:</h4>
                <ul className="text-xs text-cyan-700 space-y-1">
                  <li>· Take photos from different angles and positions</li>
                  <li>· Include distinctive visual features (artwork, architecture, signs)</li>
                  <li>· Capture different lighting conditions if possible</li>
                  <li>· Avoid photos with too many people blocking the view</li>
                  <li>· More images (5-10) generally improve matching accuracy</li>
                </ul>
              </div>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-100 bg-gray-50 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSaving || selectedFiles.length === 0}
            className={`flex items-center gap-2 px-6 py-2 rounded-lg transition-colors ${
              isSaving || selectedFiles.length === 0
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-cyan-600 text-white hover:bg-cyan-700'
            }`}
          >
            {isSaving ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <ImageIcon className="w-4 h-4" />
                Create Room
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VprRoomEditor;
